import type {
  Model,
  View,
  ViewType,
  ElementView,
  RelationshipView,
  Relationship,
  SystemContextView,
  ContainerView,
  ComponentView,
  DeploymentView,
  DeploymentNode,
} from '../types'
import type { ElementInfo } from './elementFactory'

/**
 * Information about deployment nodes for rendering
 */
export interface DeploymentNodeInfo {
  id: string
  name: string
  technology?: string
  instances?: string
  environment: string
  parentId?: string
  children: DeploymentNodeInfo[]
  infrastructureNodes: Array<{
    id: string
    name: string
    technology?: string
    description?: string
  }>
  softwareSystemInstances: Array<{
    id: string
    softwareSystemId: string
    name: string
  }>
  containerInstances: Array<{
    id: string
    containerId: string
    name: string
  }>
}

/**
 * Resolve elements to show in a view when the elements array is empty
 * This handles the case where views use "include *" without explicit element positions
 */
export function resolveViewElements(
  view: View,
  viewType: ViewType,
  model: Model,
  elementMap: Map<string, ElementInfo>,
  relationshipMap: Map<string, Relationship>
): { elements: ElementView[]; relationships: RelationshipView[] } {
  console.log('[viewElementResolver] resolving for viewType:', viewType)
  console.log('[viewElementResolver] view.elements:', view.elements)
  console.log('[viewElementResolver] model:', model)

  // If view already has elements, use them
  if (view.elements && view.elements.length > 0) {
    console.log('[viewElementResolver] Using existing view elements')
    return {
      elements: view.elements,
      relationships: view.relationships || [],
    }
  }

  // Otherwise, compute elements based on view type
  const elementIds = new Set<string>()
  const relationshipIds = new Set<string>()

  switch (viewType) {
    case 'SystemLandscape':
      // Include all people and software systems
      model.people?.forEach(p => elementIds.add(p.id))
      model.softwareSystems?.forEach(ss => elementIds.add(ss.id))
      break

    case 'SystemContext': {
      const scView = view as SystemContextView
      const targetSystemId = scView.softwareSystemId

      // Include the target software system
      elementIds.add(targetSystemId)

      // Include all people
      model.people?.forEach(p => elementIds.add(p.id))

      // Include other software systems that have relationships with the target
      model.softwareSystems?.forEach(ss => {
        if (ss.id !== targetSystemId) {
          // Check if this system has relationships with the target
          const hasRelationship = ss.relationships?.some(
            r => r.destinationId === targetSystemId || r.sourceId === targetSystemId
          )
          if (hasRelationship) {
            elementIds.add(ss.id)
          }
        }
      })
      break
    }

    case 'Container': {
      const cView = view as ContainerView
      const targetSystemId = cView.softwareSystemId
      console.log('[viewElementResolver] Container view - softwareSystemId:', targetSystemId)
      console.log('[viewElementResolver] Available software systems:', model.softwareSystems?.map(ss => ({ id: ss.id, name: ss.name })))

      // Find the target software system
      const targetSystem = model.softwareSystems?.find(ss => ss.id === targetSystemId)
      console.log('[viewElementResolver] Found target system:', targetSystem ? targetSystem.name : 'NOT FOUND')
      if (!targetSystem) break

      // Include all containers in the target system
      console.log('[viewElementResolver] Containers in system:', targetSystem.containers?.map(c => ({ id: c.id, name: c.name })))
      targetSystem.containers?.forEach(c => elementIds.add(c.id))

      // Include people that interact with these containers
      model.people?.forEach(person => {
        person.relationships?.forEach(rel => {
          // Check if relationship target is a container in this system
          const targetContainer = targetSystem.containers?.find(c => c.id === rel.destinationId)
          if (targetContainer) {
            elementIds.add(person.id)
          }
        })
      })

      // Include external software systems that interact with containers
      model.softwareSystems?.forEach(ss => {
        if (ss.id !== targetSystemId) {
          ss.relationships?.forEach(rel => {
            const targetContainer = targetSystem.containers?.find(c => c.id === rel.destinationId)
            if (targetContainer) {
              elementIds.add(ss.id)
            }
          })
        }
      })
      break
    }

    case 'Component': {
      const compView = view as ComponentView
      const targetContainerId = compView.containerId

      // Find the container and its parent system
      let targetContainer = null
      let parentSystem = null

      for (const ss of model.softwareSystems || []) {
        const container = ss.containers?.find(c => c.id === targetContainerId)
        if (container) {
          targetContainer = container
          parentSystem = ss
          break
        }
      }

      if (!targetContainer) break

      // Include all components in the target container
      targetContainer.components?.forEach(comp => elementIds.add(comp.id))

      // Include other containers that interact with these components
      parentSystem?.containers?.forEach(c => {
        if (c.id !== targetContainerId) {
          c.relationships?.forEach(rel => {
            const targetComp = targetContainer.components?.find(comp => comp.id === rel.destinationId)
            if (targetComp) {
              elementIds.add(c.id)
            }
          })
        }
      })
      break
    }

    case 'Deployment': {
      // Deployment views are handled specially - we return the deployment node structure
      // The actual rendering will use resolveDeploymentNodes
      break
    }

    case 'Dynamic': {
      // Dynamic views show a sequence of interactions
      // For now, return empty and let the view relationships handle it
      break
    }

    default:
      // For other view types, return empty for now
      break
  }

  // Collect relationships between resolved elements
  relationshipMap.forEach((rel, id) => {
    if (elementIds.has(rel.sourceId) && elementIds.has(rel.destinationId)) {
      relationshipIds.add(id)
    }
  })

  // Also check relationships defined on elements in the element map
  elementIds.forEach(elementId => {
    const elementInfo = elementMap.get(elementId)
    if (!elementInfo) return

    // Find relationships in model for this element
    findRelationshipsForElement(model, elementId, (rel) => {
      if (elementIds.has(rel.sourceId) && elementIds.has(rel.destinationId)) {
        relationshipIds.add(rel.id)
      }
    })
  })

  // Convert to ElementView array
  const elements: ElementView[] = Array.from(elementIds).map(id => ({ id }))
  const relationships: RelationshipView[] = Array.from(relationshipIds).map(id => ({ id }))

  console.log('[viewElementResolver] Resolved elements:', elements)
  console.log('[viewElementResolver] Resolved relationships:', relationships)

  return { elements, relationships }
}

/**
 * Find relationships for a specific element in the model
 */
function findRelationshipsForElement(
  model: Model,
  elementId: string,
  callback: (rel: Relationship) => void
): void {
  // Check people
  model.people?.forEach(person => {
    if (person.id === elementId) {
      person.relationships?.forEach(callback)
    }
  })

  // Check software systems
  model.softwareSystems?.forEach(ss => {
    if (ss.id === elementId) {
      ss.relationships?.forEach(callback)
    }

    // Check containers
    ss.containers?.forEach(container => {
      if (container.id === elementId) {
        container.relationships?.forEach(callback)
      }

      // Check components
      container.components?.forEach(component => {
        if (component.id === elementId) {
          component.relationships?.forEach(callback)
        }
      })
    })
  })
}

/**
 * Resolve deployment nodes for a deployment view
 */
export function resolveDeploymentNodes(
  view: DeploymentView,
  model: Model,
  _elementMap: Map<string, ElementInfo>
): DeploymentNodeInfo[] {
  const environment = view.environment
  const nodes: DeploymentNodeInfo[] = []

  // Find deployment nodes for this environment
  const deploymentNodes = model.deploymentNodes?.filter(
    node => node.environment === environment
  ) || []

  function processNode(node: DeploymentNode, parentId?: string): DeploymentNodeInfo {
    const nodeInfo: DeploymentNodeInfo = {
      id: node.id,
      name: node.name,
      technology: node.technology,
      instances: node.instances,
      environment: node.environment,
      parentId,
      children: [],
      infrastructureNodes: [],
      softwareSystemInstances: [],
      containerInstances: [],
    }

    // Process infrastructure nodes
    node.infrastructureNodes?.forEach(infra => {
      nodeInfo.infrastructureNodes.push({
        id: infra.id,
        name: infra.name,
        technology: infra.technology,
        description: infra.description,
      })
    })

    // Process software system instances
    node.softwareSystemInstances?.forEach(instance => {
      const system = model.softwareSystems?.find(ss => ss.id === instance.softwareSystemId)
      nodeInfo.softwareSystemInstances.push({
        id: instance.id,
        softwareSystemId: instance.softwareSystemId,
        name: system?.name || `System Instance ${instance.instanceId}`,
      })
    })

    // Process container instances
    node.containerInstances?.forEach(instance => {
      let containerName = `Container Instance ${instance.instanceId}`
      for (const ss of model.softwareSystems || []) {
        const container = ss.containers?.find(c => c.id === instance.containerId)
        if (container) {
          containerName = container.name
          break
        }
      }
      nodeInfo.containerInstances.push({
        id: instance.id,
        containerId: instance.containerId,
        name: containerName,
      })
    })

    // Process child nodes recursively
    node.children?.forEach(child => {
      nodeInfo.children.push(processNode(child, node.id))
    })

    return nodeInfo
  }

  deploymentNodes.forEach(node => {
    nodes.push(processNode(node))
  })

  return nodes
}

/**
 * Get all element IDs from deployment nodes (for relationship filtering)
 */
export function getDeploymentElementIds(nodes: DeploymentNodeInfo[]): Set<string> {
  const ids = new Set<string>()

  function collectIds(node: DeploymentNodeInfo) {
    ids.add(node.id)
    node.infrastructureNodes.forEach(infra => ids.add(infra.id))
    node.softwareSystemInstances.forEach(instance => ids.add(instance.id))
    node.containerInstances.forEach(instance => ids.add(instance.id))
    node.children.forEach(collectIds)
  }

  nodes.forEach(collectIds)
  return ids
}
